import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Trash2, Check, X, Plus, Home, Store, Save, FileText, Download, MessageCircle, Mail, ImagePlus, Loader2, Globe } from "lucide-react";
import { adminGet, adminPut, adminPost, adminDelete, adminUpload, getToken } from "../lib/adminApi";
import { photoUrl, API } from "../lib/api";

const inputCls = "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-white placeholder-white/40 outline-none focus:border-white/40";

// --- Contact rapide (WhatsApp / email) : aucun compte, aucune clé requis ---
// Normalise un numéro pour WhatsApp : chiffres uniquement, préfixe Mali (223) si numéro local à 8 chiffres.
const normalizePhone = (phone) => {
  if (!phone) return "";
  let n = String(phone).replace(/\D/g, "");
  if (n.startsWith("00")) n = n.slice(2);
  if (n.length === 8) n = "223" + n; // numéro malien local
  return n;
};
const waLink = (phone, text) => {
  const n = normalizePhone(phone);
  if (!n) return null;
  return `https://wa.me/${n}?text=${encodeURIComponent(text)}`;
};
const mailtoLink = (email, subject, body) => {
  if (!email) return null;
  return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};
const openLink = (url, err) => {
  if (!url) return toast.error(err);
  window.open(url, "_blank");
};

function Card({ title, desc, children, action }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 sm:p-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display font-semibold text-xl">{title}</h2>
          {desc && <p className="mt-1 text-sm text-white/60">{desc}</p>}
        </div>
        {action}
      </div>
      <div className="mt-6">{children}</div>
    </div>
  );
}

const Badge = ({ children, tone = "default" }) => {
  const tones = {
    default: "bg-white/10 text-white/70",
    green: "bg-emerald-500/20 text-emerald-300",
    amber: "bg-amber-500/20 text-amber-300",
    red: "bg-red-500/20 text-red-300",
  };
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${tones[tone]}`}>{children}</span>;
};

const fmt = (d) => (d ? new Date(d).toLocaleString("fr-FR") : "");

// ---------------- Devis & demandes ----------------
const LEAD_TABS = [
  { key: "quotes", label: "Devis" },
  { key: "materials", label: "Matériaux" },
  { key: "artisanReqs", label: "Demandes artisans" },
  { key: "issues", label: "Pannes" },
];

export function ConstructionLeads() {
  const [sub, setSub] = useState("quotes");
  const [data, setData] = useState({ quotes: null, materials: null, artisanReqs: null, issues: null });

  const load = () => {
    adminGet("/admin/construction/quotes").then((d) => setData((s) => ({ ...s, quotes: d }))).catch(() => {});
    adminGet("/admin/construction/material-orders").then((d) => setData((s) => ({ ...s, materials: d }))).catch(() => {});
    adminGet("/admin/construction/artisan-requests").then((d) => setData((s) => ({ ...s, artisanReqs: d }))).catch(() => {});
    adminGet("/admin/construction/issues").then((d) => setData((s) => ({ ...s, issues: d }))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (kind, id, status) => {
    try {
      await adminPut(`/admin/construction/${kind}/${id}`, { status });
      toast.success("Statut mis à jour");
      load();
    } catch { toast.error("Erreur"); }
  };

  const list = data[sub];

  return (
    <Card title="Devis & demandes" desc="Toutes les demandes reçues via TIRERA Construction & Habitat.">
      <div className="mb-6 flex flex-wrap gap-2">
        {LEAD_TABS.map((t) => (
          <button key={t.key} data-testid={`btp-leadtab-${t.key}`} onClick={() => setSub(t.key)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium ${sub === t.key ? "bg-white text-navy" : "bg-white/5 text-white/70 hover:bg-white/10"}`}>
            {t.label}{Array.isArray(data[t.key]) ? ` (${data[t.key].length})` : ""}
          </button>
        ))}
      </div>

      {!list ? <p className="text-white/50">Chargement…</p> : list.length === 0 ? (
        <p className="text-white/50" data-testid="btp-leads-empty">Aucune demande pour le moment.</p>
      ) : (
        <div className="space-y-3">
          {list.map((m) => (
            <div key={m.id} data-testid="btp-lead-item" className="rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium">{m.name} <span className="text-white/50">· {m.phone}</span></span>
                <span className="text-xs text-white/40">{fmt(m.created_at)}</span>
              </div>
              {sub === "quotes" && <div className="mt-1 text-sm text-white/80">{m.service} <Badge>{m.kind}</Badge> {m.city && <span className="text-white/50">· {m.city}</span>}</div>}
              {sub === "materials" && <div className="mt-1 text-sm text-white/80">{m.material}{m.quantity ? ` · ${m.quantity}` : ""} {m.city && <span className="text-white/50">· {m.city}</span>}</div>}
              {sub === "artisanReqs" && <div className="mt-1 text-sm text-white/80">Pour <b>{m.artisan_name}</b> ({m.trade})</div>}
              {sub === "issues" && <div className="mt-1 text-sm text-white/80">🔧 {m.property_ref}</div>}
              {(m.details || m.message || m.issue) && <p className="mt-2 text-sm text-white/60 whitespace-pre-wrap">{m.details || m.message || m.issue}</p>}

              {/* Contact rapide : répondre au client par WhatsApp ou email (1 clic, sans compte) */}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  data-testid={`lead-whatsapp-${m.id}`}
                  onClick={() => openLink(
                    waLink(m.phone, `Bonjour ${m.name || ""}, TIRERA Construction & Habitat fait suite à votre demande${m.service ? ` concernant « ${m.service} »` : m.material ? ` de matériaux (${m.material})` : ""}. `),
                    "Numéro de téléphone manquant"
                  )}
                  className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/20 text-emerald-300 px-3 py-1 text-xs font-medium hover:bg-emerald-500/30">
                  <MessageCircle size={13} /> Répondre par WhatsApp
                </button>
                {m.email && (
                  <button
                    data-testid={`lead-email-${m.id}`}
                    onClick={() => openLink(
                      mailtoLink(m.email, `TIRERA Construction & Habitat — votre demande${m.service ? ` : ${m.service}` : ""}`, `Bonjour ${m.name || ""},\n\nNous faisons suite à votre demande. Voici notre proposition :\n\n`),
                      "Adresse email manquante"
                    )}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium hover:bg-white/20">
                    <Mail size={13} /> Répondre par email
                  </button>
                )}
              </div>

              {(sub === "quotes" || sub === "issues") && (
                <div className="mt-3 flex items-center gap-2">
                  <Badge tone={m.status === "traité" || m.status === "résolu" ? "green" : m.status === "en cours" ? "amber" : "default"}>{m.status}</Badge>
                  {sub === "quotes" && ["nouveau", "en cours", "traité"].map((s) => (
                    <button key={s} onClick={() => setStatus("quotes", m.id, s)} className="rounded-full bg-white/10 px-2.5 py-1 text-xs hover:bg-white/20">{s}</button>
                  ))}
                  {sub === "issues" && ["ouvert", "en cours", "résolu"].map((s) => (
                    <button key={s} onClick={() => setStatus("issues", m.id, s)} className="rounded-full bg-white/10 px-2.5 py-1 text-xs hover:bg-white/20">{s}</button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ---------------- Artisans ----------------
export function ConstructionArtisans() {
  const [list, setList] = useState(null);
  const load = () => adminGet("/admin/construction/artisans").then(setList).catch(() => toast.error("Erreur"));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await adminPut(`/admin/construction/artisans/${id}`, { status }); toast.success("Mis à jour"); load(); }
    catch { toast.error("Erreur"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer cet artisan ?")) return;
    try { await adminDelete(`/admin/construction/artisans/${id}`); toast.success("Supprimé"); load(); }
    catch { toast.error("Erreur"); }
  };

  return (
    <Card title="Artisans" desc="Validez les profils pour qu'ils apparaissent dans l'annuaire public.">
      {!list ? <p className="text-white/50">Chargement…</p> : list.length === 0 ? (
        <p className="text-white/50" data-testid="btp-artisans-empty">Aucun artisan inscrit.</p>
      ) : (
        <div className="space-y-3">
          {list.map((a) => (
            <div key={a.id} data-testid="btp-artisan-item" className="rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium">{a.name} <span className="text-white/50">· {a.trade} · {a.zone}</span></span>
                <Badge tone={a.status === "approved" ? "green" : a.status === "rejected" ? "red" : "amber"}>{a.status}</Badge>
              </div>
              <div className="mt-1 text-sm text-white/60">📞 {a.phone}{a.email ? ` · ${a.email}` : ""} · ⭐ {a.rating} ({a.rating_count})</div>
              {a.bio && <p className="mt-1 text-sm text-white/50">{a.bio}</p>}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button onClick={() => setStatus(a.id, "approved")} data-testid={`artisan-approve-${a.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/20 text-emerald-300 px-3 py-1 text-xs font-medium hover:bg-emerald-500/30"><Check size={13} /> Valider</button>
                <button onClick={() => setStatus(a.id, "rejected")} className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium hover:bg-white/20"><X size={13} /> Rejeter</button>
                <button onClick={() => remove(a.id)} className="inline-flex items-center gap-1.5 rounded-full bg-red-500/20 text-red-300 px-3 py-1 text-xs font-medium hover:bg-red-500/30"><Trash2 size={13} /> Supprimer</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ---------------- Biens (gestion immobilière) ----------------
const EMPTY = { label: "", type: "appartement", address: "", description: "", tenant: "", tenant_email: "", tenant_phone: "", rent: 0, rent_status: "impaye", contract: "", notes: "", photos: [], is_published: false, tenant_password: "" };

export function ConstructionProperties() {
  const [list, setList] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => adminGet("/admin/construction/properties").then(setList).catch(() => toast.error("Erreur"));
  const loadReceipts = () => adminGet("/admin/construction/receipts").then(setReceipts).catch(() => {});
  useEffect(() => { load(); loadReceipts(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const reset = () => { setForm(EMPTY); setEditId(null); setShowForm(false); };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const { path } = await adminUpload("/admin/construction/upload-photo", file);
        setForm((f) => ({ ...f, photos: [...(f.photos || []), path] }));
      }
      toast.success("Photo(s) ajoutée(s)");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'envoi de la photo");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  const removePhoto = (p) => setForm((f) => ({ ...f, photos: (f.photos || []).filter((x) => x !== p) }));

  const save = async () => {
    if (!form.label) return toast.error("Nom du bien requis");
    const body = { ...form, rent: Number(form.rent) || 0 };
    try {
      if (editId) await adminPut(`/admin/construction/properties/${editId}`, body);
      else await adminPost("/admin/construction/properties", body);
      toast.success("Bien enregistré");
      reset(); load();
    } catch { toast.error("Erreur"); }
  };

  const edit = (p) => { setForm({ ...EMPTY, ...p }); setEditId(p.id); setShowForm(true); };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce bien ?")) return;
    try { await adminDelete(`/admin/construction/properties/${id}`); toast.success("Supprimé"); load(); } catch { toast.error("Erreur"); }
  };
  const toggleRent = async (p) => {
    try {
      if (p.rent_status === "a_jour") {
        await adminPut(`/admin/construction/properties/${p.id}`, { ...p, rent_status: "impaye" });
        toast.success("Loyer marqué impayé");
      } else {
        const res = await adminPost(`/admin/construction/properties/${p.id}/mark-paid`, {});
        toast.success(res.emailed ? "Loyer payé — reçu généré et envoyé au locataire" : "Loyer payé — reçu généré");
        loadReceipts();
      }
      load();
    } catch { toast.error("Erreur"); }
  };

  const receiptUrl = (r) => `${API}/admin/construction/receipts/${r.id}/pdf?auth=${encodeURIComponent(getToken() || "")}`;

  return (
    <Card title="Gestion immobilière" desc="Vos appartements et magasins, locataires, loyers et contrats."
      action={<button onClick={() => (showForm ? reset() : setShowForm(true))} data-testid="property-add-btn" className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-navy hover:bg-white/90"><Plus size={15} /> Ajouter</button>}>

      {showForm && (
        <div className="mb-6 rounded-2xl border border-white/10 bg-white/5 p-5" data-testid="property-form">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block"><span className="mb-1 block text-xs text-white/60">Nom du bien *</span><input value={form.label} onChange={(e) => set("label", e.target.value)} data-testid="property-label" className={inputCls} placeholder="Appartement A3, Magasin Centre…" /></label>
            <label className="block"><span className="mb-1 block text-xs text-white/60">Type</span>
              <select value={form.type} onChange={(e) => set("type", e.target.value)} className={inputCls}><option value="appartement">Appartement</option><option value="magasin">Magasin</option></select>
            </label>
            <label className="block"><span className="mb-1 block text-xs text-white/60">Adresse</span><input value={form.address} onChange={(e) => set("address", e.target.value)} className={inputCls} /></label>
            <label className="block"><span className="mb-1 block text-xs text-white/60">Locataire</span><input value={form.tenant} onChange={(e) => set("tenant", e.target.value)} className={inputCls} /></label>
            <label className="block"><span className="mb-1 block text-xs text-white/60">Email locataire (pour l'envoi du reçu)</span><input type="email" value={form.tenant_email} onChange={(e) => set("tenant_email", e.target.value)} data-testid="property-tenant-email" className={inputCls} placeholder="locataire@email.com" /></label>
            <label className="block"><span className="mb-1 block text-xs text-white/60">Téléphone locataire (pour relance WhatsApp)</span><input value={form.tenant_phone} onChange={(e) => set("tenant_phone", e.target.value)} data-testid="property-tenant-phone" className={inputCls} placeholder="+223 92 00 10 10" /></label>
            <label className="block sm:col-span-2"><span className="mb-1 block text-xs text-white/60">Mot de passe locataire {editId && <em className="text-white/40">(laisser vide pour ne pas changer)</em>}</span><input value={form.tenant_password} onChange={(e) => set("tenant_password", e.target.value)} data-testid="property-tenant-password" className={inputCls} placeholder="Mot de passe pour l'espace locataire" /></label>
            <label className="block"><span className="mb-1 block text-xs text-white/60">Loyer (FCFA)</span><input type="number" value={form.rent} onChange={(e) => set("rent", e.target.value)} className={inputCls} /></label>
            <label className="block"><span className="mb-1 block text-xs text-white/60">Statut du loyer</span>
              <select value={form.rent_status} onChange={(e) => set("rent_status", e.target.value)} className={inputCls}><option value="impaye">Impayé</option><option value="a_jour">À jour</option></select>
            </label>
            <label className="block sm:col-span-2"><span className="mb-1 block text-xs text-white/60">Contrat / notes</span><textarea rows={2} value={form.contract} onChange={(e) => set("contract", e.target.value)} className={inputCls} /></label>
            <label className="block sm:col-span-2"><span className="mb-1 block text-xs text-white/60">Description (affichée sur le catalogue public)</span><textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="property-description" className={inputCls} placeholder="3 chambres, salon, cour, eau & électricité…" /></label>
          </div>

          {/* Photos */}
          <div className="mt-4">
            <span className="mb-2 block text-xs text-white/60">Photos du bien</span>
            <div className="flex flex-wrap items-center gap-3">
              {(form.photos || []).map((p) => (
                <div key={p} className="relative h-24 w-32 overflow-hidden rounded-xl border border-white/15">
                  <img src={photoUrl(p)} alt="" className="h-full w-full object-cover" />
                  <button type="button" onClick={() => removePhoto(p)} data-testid="property-photo-remove" className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white hover:bg-red-500/80"><X size={13} /></button>
                </div>
              ))}
              <label className="flex h-24 w-32 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-white/25 text-white/60 hover:border-white/50 hover:text-white/80" data-testid="property-photo-upload">
                {uploading ? <Loader2 size={20} className="animate-spin" /> : <ImagePlus size={20} />}
                <span className="text-[11px]">{uploading ? "Envoi…" : "Ajouter"}</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} disabled={uploading} />
              </label>
            </div>
          </div>

          {/* Publication */}
          <label className="mt-4 flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 cursor-pointer">
            <input type="checkbox" checked={!!form.is_published} onChange={(e) => set("is_published", e.target.checked)} data-testid="property-publish" className="h-4 w-4 accent-emerald-500" />
            <span className="flex items-center gap-2 text-sm"><Globe size={15} /> Publier ce bien sur le site (catalogue public « À louer »)</span>
          </label>

          <div className="mt-4 flex gap-2">
            <button onClick={save} data-testid="property-save" className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-navy hover:bg-white/90"><Save size={15} /> {editId ? "Enregistrer" : "Ajouter"}</button>
            <button onClick={reset} className="rounded-full bg-white/10 px-5 py-2.5 text-sm font-medium hover:bg-white/20">Annuler</button>
          </div>
        </div>
      )}

      {!list ? <p className="text-white/50">Chargement…</p> : list.length === 0 ? (
        <p className="text-white/50" data-testid="btp-properties-empty">Aucun bien enregistré.</p>
      ) : (
        <div className="space-y-3">
          {list.map((p) => (
            <div key={p.id} data-testid="btp-property-item" className="rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="flex items-center gap-2 font-medium">{p.type === "magasin" ? <Store size={16} /> : <Home size={16} />} {p.label}</span>
                <div className="flex items-center gap-2">
                  {p.is_published && <Badge tone="green">Publié</Badge>}
                  <Badge tone={p.rent_status === "a_jour" ? "green" : "red"}>{p.rent_status === "a_jour" ? "Loyer à jour" : "Loyer impayé"}</Badge>
                </div>
              </div>
              {Array.isArray(p.photos) && p.photos.length > 0 && (
                <div className="mt-3 flex gap-2 overflow-x-auto">
                  {p.photos.map((ph) => (
                    <img key={ph} src={photoUrl(ph)} alt="" className="h-16 w-20 shrink-0 rounded-lg object-cover border border-white/10" />
                  ))}
                </div>
              )}
              <div className="mt-1 text-sm text-white/60">
                {p.address && <span>{p.address} · </span>}
                {p.tenant ? <span>Locataire : {p.tenant} · </span> : <span>Vacant · </span>}
                {p.rent ? <span>{p.rent.toLocaleString("fr-FR")} FCFA/mois</span> : null}
              </div>
              {p.contract && <p className="mt-1 text-sm text-white/50">{p.contract}</p>}
              {p.access_code && (
                <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                  <span className="text-xs text-white/60">Accès locataire :</span>
                  <code className="rounded bg-white/10 px-2 py-0.5 text-xs font-semibold text-amber-300">{p.access_code}</code>
                  <button
                    data-testid={`property-share-access-${p.id}`}
                    onClick={() => openLink(
                      waLink(p.tenant_phone, `Bonjour ${p.tenant || ""}, voici votre accès à votre Espace locataire TIRERA GROUP pour consulter et télécharger vos reçus de loyer :\n\n${window.location.origin}/locataire\nCode d'accès : ${p.access_code}\n\nVous pouvez aussi vous connecter avec votre téléphone/email et votre mot de passe.`),
                      "Ajoutez le téléphone du locataire pour partager l'accès par WhatsApp"
                    )}
                    className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/20 text-emerald-300 px-3 py-1 text-xs font-medium hover:bg-emerald-500/30">
                    <MessageCircle size={13} /> Partager l'accès par WhatsApp
                  </button>
                </div>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button onClick={() => toggleRent(p)} data-testid={`property-toggle-rent-${p.id}`} className="rounded-full bg-white/10 px-3 py-1 text-xs font-medium hover:bg-white/20">
                  Marquer {p.rent_status === "a_jour" ? "impayé" : "payé"}
                </button>
                {p.rent_status !== "a_jour" && (
                  <button
                    data-testid={`property-remind-whatsapp-${p.id}`}
                    onClick={() => openLink(
                      waLink(p.tenant_phone, `Bonjour ${p.tenant || ""}, ceci est un rappel concernant le loyer${p.label ? ` de « ${p.label} »` : ""}${p.rent ? ` d'un montant de ${Number(p.rent).toLocaleString("fr-FR")} FCFA` : ""}. Merci de bien vouloir régulariser votre paiement. TIRERA GROUP.`),
                      "Ajoutez le téléphone du locataire pour relancer par WhatsApp"
                    )}
                    className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/20 text-emerald-300 px-3 py-1 text-xs font-medium hover:bg-emerald-500/30">
                    <MessageCircle size={13} /> Relancer par WhatsApp
                  </button>
                )}
                <button onClick={() => edit(p)} className="rounded-full bg-white/10 px-3 py-1 text-xs font-medium hover:bg-white/20">Modifier</button>
                <button onClick={() => remove(p.id)} className="inline-flex items-center gap-1.5 rounded-full bg-red-500/20 text-red-300 px-3 py-1 text-xs font-medium hover:bg-red-500/30"><Trash2 size={13} /> Supprimer</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {receipts.length > 0 && (
        <div className="mt-8 border-t border-white/10 pt-6" data-testid="receipts-history">
          <h3 className="flex items-center gap-2 font-display font-semibold"><FileText size={18} /> Historique des reçus de loyer</h3>
          <div className="mt-4 space-y-2">
            {receipts.map((r) => (
              <div key={r.id} data-testid="receipt-item" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3">
                <div className="min-w-0">
                  <span className="font-medium">{r.receipt_number}</span>
                  <span className="text-white/50"> · {r.property_label}{r.tenant ? ` · ${r.tenant}` : ""}</span>
                  <div className="text-xs text-white/40">{r.period} · {(r.amount || 0).toLocaleString("fr-FR")} FCFA · {fmt(r.created_at)}</div>
                </div>
                <a href={receiptUrl(r)} target="_blank" rel="noopener noreferrer" data-testid={`receipt-download-${r.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-xs font-semibold text-navy hover:bg-white/90">
                  <Download size={14} /> Reçu PDF
                </a>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
}


// ---------------- Comptes locataires (validation) ----------------
export function ConstructionTenantRequests() {
  const [list, setList] = useState(null);
  const [properties, setProperties] = useState([]);
  const [sel, setSel] = useState({});

  const load = () => adminGet("/admin/tenant-requests").then(setList).catch(() => toast.error("Erreur"));
  useEffect(() => {
    load();
    adminGet("/admin/construction/properties").then(setProperties).catch(() => {});
  }, []);

  const approve = async (rid) => {
    const pid = sel[rid];
    if (!pid) return toast.error("Choisissez d'abord le bien à lier");
    try {
      await adminPost(`/admin/tenant-requests/${rid}/approve`, { property_id: pid });
      toast.success("Compte locataire activé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec");
    }
  };

  const remove = async (rid) => {
    try {
      await adminDelete(`/admin/tenant-requests/${rid}`);
      toast.success("Demande supprimée");
      load();
    } catch {
      toast.error("Échec");
    }
  };

  const propLabel = (pid) => properties.find((p) => p.id === pid)?.label || pid;

  return (
    <Card title="Comptes locataires" desc="Validez les demandes de compte et liez chaque locataire à son bien.">
      {!list ? (
        <p className="text-white/50">Chargement…</p>
      ) : list.length === 0 ? (
        <p className="text-white/50">Aucune demande de compte pour le moment.</p>
      ) : (
        <div className="space-y-3">
          {list.map((r) => (
            <div key={r.id} data-testid={`tenant-request-${r.id}`} className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium">{r.name}</span>
                <Badge tone={r.status === "pending" ? "amber" : "green"}>{r.status === "pending" ? "En attente" : "Activé"}</Badge>
              </div>
              <div className="mt-1 text-sm text-white/60">
                {r.phone && <span>{r.phone} · </span>}
                {r.email && <span>{r.email} · </span>}
                <span className="text-white/40">{fmt(r.created_at)}</span>
              </div>
              {r.logement && <p className="mt-1 text-sm text-white/70">Logement indiqué : « {r.logement} »</p>}

              {r.status === "pending" ? (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <select
                    data-testid={`tenant-request-select-${r.id}`}
                    value={sel[r.id] || ""}
                    onChange={(e) => setSel((s) => ({ ...s, [r.id]: e.target.value }))}
                    className="rounded-xl border border-white/15 bg-navy px-3 py-2 text-sm text-white outline-none focus:border-white/40">
                    <option value="">Lier au bien…</option>
                    {properties.map((p) => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                  <button onClick={() => approve(r.id)} data-testid={`tenant-request-approve-${r.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/20 text-emerald-300 px-3 py-2 text-xs font-medium hover:bg-emerald-500/30">
                    <Check size={13} /> Approuver
                  </button>
                  <button onClick={() => remove(r.id)}
                    className="inline-flex items-center gap-1.5 rounded-full bg-red-500/20 text-red-300 px-3 py-2 text-xs font-medium hover:bg-red-500/30">
                    <Trash2 size={13} /> Refuser
                  </button>
                </div>
              ) : (
                <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-white/70">
                  <Home size={14} /> Lié à : <span className="font-medium text-white">{propLabel(r.property_id)}</span>
                  <button onClick={() => remove(r.id)} className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium hover:bg-white/20">
                    <Trash2 size={13} /> Supprimer
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
